<template>
    <ModalContent stitch fill-height :help-content-id="`popup-customer-${current_entity_profile}`">
        <template slot="title">{{ $translate("title") }}</template>
        <template slot="rawcontent" v-if="customer !== null">
            <div class="customercatalogs-modal d-flex flex-column min-height-0 h-100">
                <Loading v-if="!ready" :absolute="false" />
                <Placeholder
                    v-else-if="!catalogs_loading && catalogs_count === 0"
                    :title="$translate('noCatalogFound')"
                    :description="$translate('noCatalogFoundDescription')"
                    :actions="placeholderActions"
                    @on-action="onPlaceholderAction"
                />
                <div
                    v-else
                    class="customercatalogs-modal-content d-flex flex-column flex-grow-1 min-height-0"
                >
                    <div class="flex-shrink-0 mt-4 px-4">
                        <h5 class="stitch-modal__title">
                            {{ $translate("catalogsTitle") }}
                        </h5>
                        <p
                            v-html="
                                $translate('catalogsInstructions').replace(
                                    '{customer}',
                                    customer.name
                                )
                            "
                        ></p>
                    </div>
                    <div class="customercatalogs-modal-table flex-grow-1 min-height-0 overflow-y-auto">
                        <Table
                            :columns="columns"
                            :rows="catalogs"
                            :rows-count="catalogs_count"
                            :loading="catalogs_loading"
                            :filters="catalogs_filters"
                            :filters-conf="filtersConf"
                            :remote="true"
                            :show-select="true"
                            :item-key="'id'"
                            :item-value="'id'"
                            :value="allowedCatalogs"
                            :items-per-page="catalogs_filters.limit"
                            @on-filters-change="onFiltersChange"
                            @on-row-click="onRowClick"
                            @on-row-selected="onRowSelected"
                            @on-row-deselected="onRowDeselected"
                            @toggle-select-all="toggleSelectAll"
                            :single-select="current_entity_profile == 'operator'"
                        >
                            <template slot="owner_name" slot-scope="props">
                                <ProviderName
                                    v-if="props.row.provider"
                                    :provider="props.row.provider_data"
                                />
                                <ProviderName
                                    v-else-if="props.row.owner != current_entity_id"
                                    :provider="props.row.owner_data"
                                    :lookup="{ entity: props.row.owner }"
                                />
                                <template v-else>
                                    {{ current_entity_name }}
                                </template>
                            </template>
                        </Table>
                    </div>
                </div>
            </div>
        </template>
        <template slot="bottom">
            <v-btn
                v-if="catalogs_count > 0"
                class="my-2 my-sm-4 w-100 w-sm-auto"
                @click="assignDefaultCatalogs"
            >
                {{ $translate("assignDefaultCatalogs") }}
            </v-btn>
            <v-spacer />
            <v-btn class="my-2 my-sm-4 w-100 w-sm-auto" @click="hideModal">
                {{ $translate("buttons.cancel") }}
            </v-btn>
            <v-btn
                v-if="catalogs_count > 0"
                class="primary my-2 my-sm-4 w-100 w-sm-auto ml-sm-3"
                :disabled="customer_saving"
                @click="assignCatalogsClicked"
            >
                {{ saveText }}
            </v-btn>
        </template>
    </ModalContent>
</template>

<script>
import { mapState, mapActions, mapMutations } from "vuex";
import GlobalMixins from "@/mixins/global";
import Placeholder from "@/components/common/placeholder";
import EditCatalog from "@/modals/catalog";

export default {
    fillHeight: true,
    mixins: [GlobalMixins],
    components: { Placeholder },
    props: {
        params: {
            type: Object,
            required: true,
        },
    },
    computed: {
        ...mapState("customercatalogs/catalogs", [
            "catalogs",
            "catalogs_count",
            "catalogs_filters",
            "catalogs_loading",
            "catalogs_loaded",
        ]),
        ...mapState("customercatalogs", [
            "allowed_catalogs",
            "customer",
            "customer_saving",
            "customer_saving_errors",
            "ready",
        ]),
        filtersConf() {
            return [
                "search",
                {
                    field: "default",
                    type: "boolean",
                    label: this.$translate("default"),
                },
            ];
        },

        allowedCatalogs() {
            return this.allowed_catalogs
                .filter((catalog_id) =>
                    this.catalogs.find((item) => item.id == catalog_id)
                )
                .map((id) => {
                    return this.catalogs.find((catalog) => catalog.id === id);
                });
        },
        columns() {
            return [
                {
                    label: this.$translate("labels.provider"),
                    field: "owner_name",
                },
                {
                    label: this.$translate("labels.name"),
                    field: "name",
                },
                {
                    label: this.$translate("labels.products"),
                    field: "products_count",
                },
                {
                    label: this.$translate("labels.assignments"),
                    type: "number",
                    field: "assignments_count",
                },
                {
                    label: this.$translate("labels.default"),
                    type: "boolean",
                    field: "default",
                },
            ];
        },
        saveText() {
            if (this.allowed_catalogs.length > 1) {
                return this.$translate("assignCatalogs", {
                    count: this.allowed_catalogs.length,
                });
            }
            return this.$translate("assignCatalog", {
                count: this.allowed_catalogs.length,
            });
        },
        placeholderActions() {
            if (!this.catalogs_loading && this.catalogs_count === 0) {
                return [
                    {
                        action: "createCatalog",
                        text: this.$translate("buttons.createCatalog"),
                        icon: "catalog",
                        main: true,
                    },
                ];
            }
            return [];
        },
    },
    methods: {
        ...mapActions("mcl", ["hideModal", "showModal"]),
        ...mapMutations("customercatalogs/catalogs", ["updateCatalogsFilters"]),
        ...mapMutations("customercatalogs", ["updateAllowedCatalogs"]),
        ...mapActions("customercatalogs/catalogs", ["fetchCatalogs"]),
        ...mapActions("customercatalogs", [
            "assignCatalogs",
            "init",
            "changeBillingAccountId",
            "assignDefaultCatalogs",
        ]),
        onPlaceholderAction(action) {
            if (action === "createCatalog") {
                this.createCatalogAction();
            }
        },
        async createCatalogAction() {
            try {
                const result = await this.showModal({
                    component: EditCatalog,
                    params: {
                        catalog: {},
                        skipCustomerAssignment: true,
                    },
                });
                if (result) {
                    // Recharger les catalogues après création
                    await this.fetchCatalogs();
                }
            } catch (error) {
                // L'utilisateur a peut-être annulé, on ne fait rien
            }
        },
        toggleSelectAll() {
            if (this.current_entity_profile == "operator") {
                return false;
            }
            // If this.catalogs is contained on this.allowed_catalogs, deselect in this.allowed_catalogs items that are in this.catalogs
            // else, add all catalogs available on this.catalogs in this.allowed_catalogs
            const not_in_allowed = this.catalogs.filter(
                (item) => !this.allowed_catalogs.includes(item.id)
            );
            if (not_in_allowed.length) {
                // Select all
                this.updateAllowedCatalogs(
                    this.allowed_catalogs.concat(
                        not_in_allowed.map((item) => item.id)
                    )
                );
            } else {
                // Deselect all
                this.updateAllowedCatalogs(
                    this.allowed_catalogs.filter(
                        (item) =>
                            !this.catalogs.find(
                                (catalog) => catalog.id === item
                            )
                    )
                );
            }
        },

        onFiltersChange(filters) {
            this.updateCatalogsFilters(filters);
            this.fetchCatalogs();
        },
        onRowSelected(catalog) {
            if (this.current_entity_profile == "operator") {
                this.updateAllowedCatalogs([catalog.id]);
                return;
            }
            if (this.allowed_catalogs.indexOf(catalog.id) == -1) {
                this.updateAllowedCatalogs(
                    this.allowed_catalogs.concat([catalog.id])
                );
            }
        },
        onRowDeselected(catalog) {
            this.updateAllowedCatalogs(
                this.allowed_catalogs.filter((item) => item != catalog.id)
            );
        },
        onRowClick(catalog) {
            let allowed_catalogs = this.allowed_catalogs || [];
            let index = this.allowed_catalogs.indexOf(catalog.id);
            if (index == -1) {
                allowed_catalogs.push(catalog.id);
            } else {
                allowed_catalogs.splice(index, 1);
            }
            this.updateAllowedCatalogs(allowed_catalogs);
        },
        async assignCatalogsClicked() {
            let errors = {};
            if (!this.customer.name) {
                errors.name = [this.$translate("validation.required")];
            }

            if (Object.keys(errors).length) {
                this.updateCustomerSavingErrors(errors);
                return;
            }

            try {
                const response = await this.assignCatalogs({
                    instance: this.customer,
                });
                if (response.status == 201) {
                    this.addAlert(this.$translate("messages.customerCreated"));
                } else {
                    this.addAlert(this.$translate("messages.customerUpdated"));
                }
                this.hideModal({ result: response.data });
            } catch (error) {
                this.$swal({
                    title: this.$translate("messages.error"),
                    html: this.$utils.resultToSwalContent(this, error),
                    type: "error",
                });
            }
        },
    },
    created() {
        if (!this.params.customer?.id) {
            return;
        }
        this.init({
            customer: this.params.customer,
            assign_defaults: this.params.assign_defaults,
        });
    },
    i18n: {
        messages: {
            en: {
                catalogsTitle: "Sales catalogs",
                catalogsInstructions:
                    "Please choose the sales catalog(s) you want to assign to the customer.",
                default: "Default catalogs",
                title: "Assign sales catalogs",
                assignCatalogs: "Assign {count} catalogs",
                assignCatalog: "Assign {count} catalog",
                assignDefaultCatalogs: "Assign default catalogs",
                buttons: {
                    cancel: "Cancel",
                    createCatalog: "Create your first catalog",
                },
                labels: {
                    provider: "Provider",
                    name: "Name",
                    products: "Products",
                    assignments: "Assignments",
                    default: "Default",
                },
                messages: {
                    customerCreated: "Customer created",
                    customerUpdated: "Customer updated",
                    error: "Error",
                },
                noCatalogFound: "No catalog available",
                noCatalogFoundDescription:
                    "There are no catalogs available to assign to this customer. You need to create a catalog first or have catalogs assigned to you as a sales agent.",
                validation: {
                    required: "This field is required",
                },
            },
            fr: {
                catalogsTitle:
                    "Choisissez les catalogues à proposer à votre client",
                catalogsInstructions:
                    "Cette page présente vos propres catalogues de vente, ainsi que ceux que d'autres vendeurs vous ont autorisés à commercialiser en tant qu'agent commercial. Sélectionnez les catalogues que vous souhaitez mettre à disposition du client <b>{customer}</b> : il pourra alors passer commande de manière autonome. Si un même produit figure dans plusieurs catalogues avec des prix différents, c'est le prix le plus bas qui sera affiché au client.",
                default: "Catalogues par défaut",
                title: "Assigner des catalogues de vente",
                assignCatalogs: "Assigner {count} catalogues",
                assignCatalog: "Assigner {count} catalogue",
                assignDefaultCatalogs: "Assigner les catalogues par défaut",
                buttons: {
                    cancel: "Annuler",
                    createCatalog: "Créer votre premier catalogue",
                },
                labels: {
                    provider: "Fournisseur",
                    name: "Nom",
                    products: "Produits",
                    assignments: "Assignations",
                    default: "Par défaut",
                },
                messages: {
                    customerCreated: "Client créé",
                    customerUpdated: "Client mis à jour",
                    error: "Erreur",
                },
                noCatalogFound: "Aucun catalogue disponible",
                noCatalogFoundDescription:
                    "Il n'y a aucun catalogue disponible à assigner à ce client. Vous devez d'abord créer un catalogue ou avoir des catalogues assignés en tant qu'agent commercial.",
                validation: {
                    required: "Ce champ est requis",
                },
            },
        },
    },
};
</script>

<style lang="scss" scoped>
.customercatalogs-modal {
    min-height: 0;
}

.customercatalogs-modal-content {
    min-height: 0;
}

.customercatalogs-modal-table {
    min-height: 0;
    -webkit-overflow-scrolling: touch;
}
</style>
